import { Badge, Box, Button, DrawerBody, Flex, FormatNumber, HStack, IconButton, Input, Separator, Stack, Switch, Table, Tag, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, UI } from "components";
import { Styles } from "config";
import { Mask } from "config";
import { API } from "config";
import { toaster } from "config";
import useCart from "hooks/useCart";
import useCashBalance from "hooks/useCashBalance";
import useDistributor from "hooks/useDistributor";
import { useData } from "hooks/useData";
import { useEffect, useState } from "react";
import Moment from "react-moment";
import { useNavigate } from "react-router";

export default function Cart({ ...props }){

    const cart = useCart()
    const { cart:cartData, setCart } = useData()

    useEffect(() => {
        setCart({ step: 1, shipping_type: 1 })
    }, [cart.data?.id])

    return(
    <UI.Drawer size="md" title={<><UI.Icon name="cart" /> Sacola</>} {...props}>
        <DrawerBody>
            <UI.Alert size="sm" mb={4} status="warning">
                Seu carrinho expira <b><Moment fromNow children={cart.data?.cart_expire_at} /></b>. Finalize o pedido antes para não perder a reserva dos itens.
            </UI.Alert>

            { cartData.step === 2 ? <Finish /> : <General {...props} /> }
        </DrawerBody>
    </UI.Drawer>
    )
}

const General = (props) => {

    const cart = useCart()
    const { setCart } = useData()
    const itens = cart.data?.itens ?? []
    const distributor = cart.data?.distributor_from ?? {}

    const countItens = () => {
        let total = 0
        itens.map(item => { total = total + item.quantity })
        return total
    }

    return(
    <Stack gap={4}>

        <Text color="GrayText">Itens do seu pedido:</Text>

        <Table.Root size="sm" variant="line">
            <Table.Header>
                <Table.Row>
                    <Table.ColumnHeader>Produto</Table.ColumnHeader>
                    <Table.ColumnHeader>Und.</Table.ColumnHeader>
                    <Table.ColumnHeader>Subtotal</Table.ColumnHeader>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {itens.map(item => {

                    return(
                    <Table.Row>
                        <Table.Cell fontSize="xs"><Tag.Root colorPalette="orange"><Tag.Label>{item.quantity}</Tag.Label></Tag.Root> {item.product?.name}</Table.Cell>
                        <Table.Cell fontSize="xs"><FormatNumber value={item.price_und / 100} style="currency" currency="BRL" /><Badge children={`- ${item?.discount_percentage}%`} /></Table.Cell>
                        <Table.Cell><FormatNumber value={item.amount / 100} style="currency" currency="BRL" /></Table.Cell>
                    </Table.Row>
                    )
                })}
            </Table.Body>
        </Table.Root>

        <Flex justifyContent="space-between" alignItems="center">
            <Box>
                <Text fontWeight="bold">{countItens()} Itens na sacola</Text>
            </Box>

            <Box>
                <Text fontSize="xs" color="GrayText">Total do pedido:</Text>
                <Text fontSize="lg" fontWeight="bold" color="green.fg"><FormatNumber value={cart.data?.total_amount / 100} style="currency" currency="BRL" /></Text>
            </Box>
        </Flex>

        <Separator />

        <Box>
            <Text color="GrayText">Núcleo de distribuição selecionado:</Text>
            <Text fontWeight="bold" color="orange.fg" mt={2}>{distributor?.name}</Text>
            <Text color="GrayText" fontSize="xs" textTransform="capitalize">{distributor.city?.name}, {distributor.city?.state_code} - {distributor.address_neighborhood}</Text>
        </Box>

        <Separator />

        <Button colorPalette="orange" disabled={cart.data?.total_amount <= 0} onClick={() => setCart({ step: 2 })}><UI.Icon name="cart" /> Finalizar pedido <UI.Icon name="right" /></Button>
        <Button colorPalette="gray" variant="ghost" onClick={props.onClose} size="sm"><UI.Icon name="prev" /> Continuar comprando</Button>

    </Stack>
    )
}

const Finish = () => {

    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const cart = useCart()
    const cash = useCashBalance()
    const distributor = useDistributor()
    const [ loading, setLoading ] = useState(false)
    const { cart:cartData,  setCart } = useData()

    // Percentual vindo da tabela options (cash_balance_percentage); fallback 30 enquanto o cache carrega
    const cashPercent = (distributor.data?.config?.cash_balance_percentage ?? 30) / 100
    const cash_max = Math.floor(cart.data?.total_amount * cashPercent)
    const cash_utilization = cash_max <= 0 ? 0 : (cash.data >= cash_max ? cash_max : (cash.data <= 0 ? 0 : cash.data))
    const cash_available = cash.data - cash_utilization
    const restant = cart.data?.total_amount - cash_utilization <= 0 ? 0 : cart.data?.total_amount - cash_utilization
    const isMatriz = !!cart.data?.distributor_from?.is_matriz

    const getPaymentMethods = () => {
        const pm = []
        if(cash_utilization >= 1) pm.push({ method: 3, amount: cash_utilization })
        if(restant >= 1) pm.push({ method: isMatriz ? 9 : 4, amount: restant })
        return pm
    }

    const handleSubmit = async () => {
        if(!window.confirm('Tem certeza que deseja finalizar o pedido?')) return
        setLoading(true)

        try{
            await API.put('/store/cart/shipping', { shipping_type: cartData.shipping_type })
            await API.put('/store/cart/payment', { payment_methods: getPaymentMethods() })
            const finishResponse = await API.put('/store/cart/finish', { distributor_from_id: cart.data?.distributor_from?.id })

            // API retorna array quando o pedido foi segmentado por categoria (compra da Matriz)
            const responseData = finishResponse.data?.data ?? finishResponse.data
            const isSegmented = Array.isArray(responseData)
            const orders = isSegmented ? responseData : [responseData]
            const primaryId = orders[0]?.id ?? cart.data?.id

            toaster.create({ title: 'O pedido foi finalizado.', type: 'success' })
            navigate(`/compras/${primaryId}`)
            await cart.refetch()
            setCart({ payment_method: [], step: 1 })
            await queryClient.invalidateQueries('orders')
        }catch(err){
            toaster.create({ title: 'Não foi possível finalizar o pedido. Tente novamente.', description: err?.response?.data?.message, type: 'warning' })
        }

        setLoading(false)
    }

    console.log(cartData)

    return(
    <Stack gap={4}>

        <Box>
            <Form.Segment
                required
                label="Forma de entrega:"
                items={[{ label: <HStack><UI.Icon name="shipping" /> Entrega</HStack>, value: 1 }, { label: <HStack><UI.Icon name="withdraw" /> Retirada</HStack>, value: 2 }]}
                defaultValue={cartData?.shipping_type ?? 1}
                value={cartData?.shipping_type ?? 1}
                onValueChange={e => setCart({ shipping_type: e.value })}
                disabled={loading}
            />
        </Box>

        <UI.Show visible={cartData.shipping_type === 1}>
            <UI.Alert size="sm" title="Remessa para envio">
                Nossa equipe de logística irá entrar em contato para realizar a cotação do envio e agendar a entrega do seu pedido.
            </UI.Alert>
        </UI.Show>

        <UI.Show visible={cartData.shipping_type === 2}>
            <UI.Alert size="sm" title="Remessa para retirada na Matriz">
                Você selecionou o pedido para retirada, você deve ter disponibilidade de retirar o seu pedido na Matriz. Não será possível alterar o tipo de envio depois que o pedido for enviado.
            </UI.Alert>
        </UI.Show>

        <Separator />

        <Box>
            <Text mb={4} color="GrayText">Detalhamento da fatura:</Text>

            <Stack gap={1} p={4} m={4} borderWidth={1} borderRadius={4} borderStyle="dashed" borderColor="gray.200">
                <HStack alignItems="center">
                    <Text fontSize="xs" color="GrayText">Valor do pedido:</Text>
                    <Text fontSize="xs" color="gray.fg" fontWeight="bold"><UI.Number.Amount value={cart.data?.total_amount} /></Text>
                </HStack>

                <HStack alignItems="center">
                    <Text fontSize="xs" color="GrayText">Saldo utilizado:</Text>
                    <Text fontSize="xs" color="red.focusRing" fontWeight="bold">- <UI.Number.Amount value={cash_utilization} /></Text>
                </HStack>

                <HStack alignItems="center">
                    <Text fontSize="xs" color="GrayText">Saldo restante:</Text>
                    <Text fontSize="xs" color="green.fg" fontWeight="bold"><UI.Number.Amount value={cash_available} /></Text>
                </HStack>

                <UI.Show visible={cartData.shipping_type === 1}>
                    <HStack alignItems="center">
                        <Text fontSize="xs" color="GrayText">Envio:</Text>
                        <Text fontSize="xs">R$0,00 (A ser calculado)</Text>
                    </HStack>
                </UI.Show>

                <Separator />

                <HStack alignItems="center">
                    <Text fontSize="xs" color="GrayText">Restante ({isMatriz ? 'Cielo' : 'Pix'}):</Text>
                    <Text fontSize="xs" color="orange.fg" fontWeight="bold"><UI.Number.Amount value={restant} /></Text>
                </HStack>
            </Stack>

        </Box>

        <UI.Show visible={restant > 0 && !isMatriz}>
            <UI.Alert size="sm" colorPalette="orange" mx={4}>
                O valor restante de <b><UI.Number.Amount value={restant} /></b> será pago via <b>Pix</b> diretamente ao núcleo vendedor. O QR Code será gerado após a finalização do pedido.
            </UI.Alert>
        </UI.Show>

        <Separator my={4} />

        <Stack>
            <Button size="lg" colorPalette="orange" onClick={handleSubmit} loading={loading} loadingText="Finalizando pedido..."><UI.Icon name="check" /> Finalizar pedido <UI.Icon name="next" /></Button>
            <Button disabled={loading} size="xs" variant="ghost" onClick={() => setCart({ step: 1 })}><UI.Icon name="prev" /> Voltar para a sacola <UI.Icon name="cart" /></Button>
        </Stack>
    </Stack>
    )
}
